/**
 * THE CALL BECAME A MEETING — GO THERE.
 *
 * The sibling of `useTransferredCall`, mounted from the same place for the same
 * reason: an in-call verb moved you somewhere else without ringing you, and the
 * only thing that says so is an event on your own `user:` topic.
 *
 * IT LIVES IN `IncomingCallHost`, NOT IN THE CALL SURFACE. A meeting is not a
 * call, and on the desktop it must not open in a window the shell keeps keyed
 * to one — that window's close button would hang up nothing and it would never
 * close itself (`useCallSurfaceExit` states the rule at length). `AppOverlays`
 * excludes `IncomingCallHost` from a call window, so the WORKSPACE follows the
 * escalation while the call window takes itself away off `useCallLifecycle`'s
 * `escalated` flag — byte-for-byte the arrangement a merge already uses.
 *
 * TWO STEPS, AND THE ORDER MATTERS. Redeem the join code first, navigate
 * second: the meeting is not readable by this user until the redemption has
 * written their participant row, and a navigation that arrives first lands on
 * "This meeting is not available".
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@cyphex/ui';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getRealtimeClient } from '@/lib/realtime';
import { escalationFor, redeemEscalation } from '../../lib/escalation';

export function useEscalatedCall(): void {
  const navigate = useNavigate();
  const { user } = useAuth();
  const meId = user?.id ?? null;

  useEffect(() => {
    if (meId === null) return undefined;
    const realtime = getRealtimeClient();
    realtime.connect();
    return realtime.onEvent(({ event }) => {
      const invite = escalationFor(event, meId);
      if (invite === null) return;
      /* SAID BEFORE THE ROUND TRIP, not after. The call surface is about to be
         replaced by a meeting the user did not ask for, and "your call just
         disappeared" is the exact experience this whole change exists to end. */
      toast.info('This call has moved to a meeting.');
      /* THE CORNER TILE IS NOT DROPPED HERE. Navigating makes the call surface
         compact, and `CallSurface` takes an escalated call out of the live store
         the moment it is — one mechanism, the same one every other ending
         already uses, rather than two places racing to clear one slot. */
      void redeemEscalation(invite).then((entered) => {
        if (!entered) {
          /* The one way this fails that we cannot fix from here: the code was
             refused. The call is genuinely over, so the rule is the one
             `CallSurface` already follows for a call that has ended — say what
             happened and go back to the conversation, rather than leave someone
             sitting on a dead room. */
          toast.error('Could not open the meeting this call moved to.');
          navigate(ROUTES.chat);
          return;
        }
        navigate(ROUTES.meeting(invite.meetingId));
      });
    });
  }, [meId, navigate]);
}
